/* eslint-disable */

import moment from 'moment-timezone';
import Vue from 'vue';
import _ from 'lodash';
import axios from 'axios';
import { safeSnapshot } from '@/store/firestoreListen';
import { settingsWritePayload } from '@/utils/practiceOps/settingsModel';
import { applyClassicDeletes } from '@/utils/practiceOps/classicSettingsDeletes';
import { extractApiError } from '@/utils/apiError';
import {
    collection,
    doc,
    updateDoc,
    setDoc,
    arrayUnion,
    arrayRemove,
    serverTimestamp,
    deleteField
} from 'firebase/firestore';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {

    namespaced: true,
    state: {
        practicePulse: {},
        settings: []     
    },

    mutations: {

        setPracticePulse(state, payload) {
            state.practicePulse = payload;
        },

        resetPracticePulse(state) {
            state.practicePulse = {};
        },

        addItemToPracticePulse(state, payload) {
            let _temp = [...state.practicePulse];
            _temp.push(payload);
            state.practicePulse = _temp;
        },
        
        setSettings(state, payload) {
            state.settings = payload;
        },

        setUpcomingFollowups(state, payload) {
            state.upcomingFollowups = payload;
        }
    },

    getters : {        
    },

    actions: {

        async getPracticePulseStatuses({commit, dispatch}, payload) {
            try {
                let response = await axios.post(`/get-practice-pulse-statuses`, payload, { crossdomain: true });                
                return response.data;
            }
            catch(err) {
                console.log('Error Getting practice pulse statuses:', err);
            }
        },
    
        /* get pp data from firebase */
        async getFbOps({ commit, rootState, dispatch, state}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true}); 
                const ppRef = doc(rootState.db, 'practice-pulse', payload.siteID);
                const unsubscribe = safeSnapshot(ppRef, (doc) => {
                    // CRITICAL: Don't update state if we're currently dragging
                    if (window.__isDragging) {
                        return;
                    }
                    
                    // Use debounced update to prevent rapid state changes
                    const ppItem = doc.data();
                    if (ppItem) {
                        ppItem.id = doc.id;
                        dispatch('debouncedUpdatePracticePulse', ppItem);
                    }
                    dispatch('alert/setLoading', false, {root: true});        
                }, {
                    source: 'practicepulse/ops',
                    onError: (error, { denied }) => {
                        console.log('Error while getting patient flow for site:', error);
                        dispatch('alert/setLoading', false, {root: true});
                        // A denial is handled by the session-health guard (src/services/sessionHealth.js),
                        // never painted raw.
                        if (denied) return;
                        dispatch('alert/setErrored', true, {root: true});
                        dispatch('alert/setMessage', error, {root: true});
                    }
                });
                // Store the unsubscribe function to remove the listener later if needed                
                commit('customer/addFirestoreListener', unsubscribe, {root: true});
            } catch(err) {
                console.log('Error while getting patient flow for site:', err);
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        // Debounced update function to prevent rapid state changes
        debouncedUpdatePracticePulse: _.debounce(function({ commit }, ppItem) {
            // FIXED: Remove resetPracticePulse call to prevent clearing state during drag operations
            // The reset was causing everything to disappear when dragging patients to operatories
            // because Vue would re-render with empty {} before setting new data
            commit('setPracticePulse', ppItem);
        }, 250),

        async getPracticePulseOps({ commit, rootState, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true}); 
                let response = await axios.post(`/get-practice-pulse-ops`, payload, { crossdomain: true });                
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                return response.data;                
            } catch(err) {
                console.log('Error while getting practice pulse ops:', err);
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        /*
            Practice Ops (plan section 4b): today's appointment details for the cards on the board - the live Note and
            ProcDescript from the Open Dental mirror, through the repurposed POST /get-practice-pulse-ops.
                payload { siteID, aptNums: [positive int, ...] }   1-200 AptNums per call
                resolves [{ AptNum, PatNum, Note, ProcDescript, ProvNum, Op, Pattern, AptDateTime }]
            QUIET by design: no global loading flag and no error banner (unlike getPracticePulseOps above) - a background
            refresh failing must never paint over a working board. A failure resolves [] and the card keeps the document's copy.
            Goes through axios, so the demo-mask interceptor masks the Note (AptNum + Note rule); it is a JSON POST, so the
            login token is attached by authTokenInterceptor.
        */
        async getApptDetails({ commit, rootState, dispatch}, payload) {
            try {
                let response = await axios.post(`/get-practice-pulse-ops`, payload, { crossdomain: true });
                const data = response && response.data;
                if (Array.isArray(data)) return data;
                if (data && Array.isArray(data.data)) return data.data;
                return [];
            } catch(err) {
                console.log('Error while getting appointment details for the board:', err);
                return [];
            }
        },

        async sendAlert({ commit, rootState, dispatch}, payload) {            
            try {
                dispatch('alert/setLoading', true, {root: true}); 
                const ppRef = doc(rootState.db, 'practice-pulse', payload.ppFBID);
                await updateDoc(ppRef, {
                    alerts: arrayUnion(payload.alert)
                });
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setMessage', 'Alert sent successfully!', {root: true});
                // Resolves true ONLY when the ping is on the board: the page pushes it to the recipient's phone after that (web push).
                return true;
            } catch(err) {
                console.log('Error while sending Alert in practice-pulse:', err);
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                return false;
            }
        },

        async updateOps({ commit, rootState, dispatch}, payload) {
            try {
                // If patients were removed from checkin, mark their keys for deletion
                // so Firestore's merge doesn't preserve stale entries
                if (payload.deletedCheckinKeys && payload.deletedCheckinKeys.length > 0) {
                    if (!payload.ops) payload.ops = {};
                    if (!payload.ops.checkin) payload.ops.checkin = {};
                    for (const key of payload.deletedCheckinKeys) {
                        payload.ops.checkin[key] = deleteField();
                    }
                    delete payload.deletedCheckinKeys;
                }

                const ppRef = doc(rootState.db, 'practice-pulse', payload.ppFBID);
                // Use setDoc with merge: true to create document if it doesn't exist,
                // or update it if it does. This allows new customers to save settings
                // immediately without waiting for the nightly reset cron job.
                // A quick-note category / custom room the office deleted in classic Configure (payload.deletedSettingsKeys,
                // payload.deletedOpColumns): a plain merge keeps those map keys, so they go as deleteField(). The sentinels are
                // put on COPIES; payload.settings is the live store object and must never hold one.
                const body = (payload.deletedSettingsKeys || payload.deletedOpColumns)
                    ? applyClassicDeletes(payload, deleteField())
                    : payload;
                await setDoc(ppRef, body, { merge: true });
            } catch(err) {
                console.log('Error while updating ops in practice-pulse:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        /*
            Practice Ops Settings panel: write ONLY the settings keys the office changed, into the SAME place classic writes
            them (the board document practice-pulse/{siteID}, field 'settings'), merged so every key the panel does not own
            (the four dropped alert toggles, 'ops', anything newer) is left exactly as it is.
                payload { ppFBID, stored, values }   stored = the document's current settings (null before the first save);
                                                     values = { <owned settings key>: <next value> } (utils/practiceOps/settingsModel.js)
            A key that disappeared from a map (a deleted quick-note category, a cleared room name) is sent as deleteField(),
            which a plain merge would silently keep. Unlike updateOps this does NOT touch 'ops' (no read-then-write of the
            patient columns), and it RETHROWS so the panel can say the save failed instead of closing as if it worked.
        */
        async savePracticeOpsSettings({ commit, rootState, dispatch}, payload) {
            const { ppFBID, stored, values } = payload;
            const settings = settingsWritePayload(stored, values, deleteField());
            if (Object.keys(settings).length === 0) return;
            try {
                const ppRef = doc(rootState.db, 'practice-pulse', ppFBID);
                await setDoc(ppRef, { settings }, { merge: true });
            } catch(err) {
                console.log('Error while saving Practice Ops settings:', err);
                throw err;
            }
        },

        async deleteAlert({ commit, rootState, dispatch}, payload) {
            try {                
                const ppRef = doc(rootState.db, 'practice-pulse', payload.ppFBID);
                await updateDoc(ppRef, {
                    alerts: arrayRemove(payload.alert)
                });
            } catch(err) {
                console.log('Error while deleting alert in practice-pulse:', err);                
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async updateAlerts({ commit, rootState, dispatch}, payload) {
            try {                
                const ppRef = doc(rootState.db, 'practice-pulse', payload.ppFBID);
                await updateDoc(ppRef, {
                    alerts: payload.alerts
                });
            } catch(err) {
                console.log('Error while updating alert in practice-pulse:', err);                
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async updateODConfirmStatus({ commit, rootState, dispatch}, payload) {            
            try {                
                let response = await axios.post(`/update-practice-pulse-od-confirm-status`, payload, { crossdomain: true });                
                return response.data;
            } catch(err) {
                console.log('Error while updating OD Confirm status:', err);                
            }
        },

        async saveDismissedStatus({ commit, rootState, dispatch}, payload) {            
            try {                
                let response = await axios.post(`/save-practice-pulse-dismissed-status`, payload, { crossdomain: true });                
                return response.data;
            } catch(err) {
                console.log('Error while saving PP dismissed status:', err);                
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getCheckoutNotes({ commit, rootState, dispatch}, payload) {            
            try {                
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/get-practice-pulse-checkout-notes`, payload, { crossdomain: true });                
                dispatch('alert/setLoading2', false, {root: true});
                dispatch('alert/setLoaded2', true, {root: true});
                return response.data;
            } catch(err) {
                console.log('Error while getting Practice Pulse checkout notes:', err);                
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
            }
        },

        async getPracticePulseReport({ commit, rootState, dispatch}, payload) {            
            try {                
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/get-practice-pulse-report`, payload, { crossdomain: true });                
                dispatch('alert/setLoading2', false, {root: true});
                dispatch('alert/setLoaded2', true, {root: true});
                return response.data;
            } catch(err) {
                console.log('Error while getting Practice Pulse report:', err);                
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
            }
        },

        /*
            Practice Ops report page: the RANGE report, POST /get-practice-pulse-report { siteID, from, to, provNum? }
            -> { visits[], tiles, daily[], providers[], thresholdMin } (practice-pulse-service lib/visits.js). A JSON POST, so the
            login token goes on automatically (authTokenInterceptor). Unlike getPracticePulseReport above this does NOT touch the
            global alert/loading flags (the page draws its own loading and error states) and it THROWS on a failure, with the
            server's own words (extractApiError), so the page can show why instead of an empty table.
        */
        async getPracticeOpsReport({ commit, rootState, dispatch}, payload) {
            try {
                const response = await axios.post(`/get-practice-pulse-report`, payload, { crossdomain: true });
                return response.data;
            } catch(err) {
                console.log('Error while getting the Practice Ops report:', err);
                const failure = new Error(extractApiError(err, 'Could not load the report. Please try again.'));
                failure.status = err && err.response ? err.response.status : null;
                throw failure;
            }
        },

        async saveCheckoutNotes({ commit, rootState, dispatch}, payload) {
            try {                
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/save-practice-pulse-checkout-notes`, payload, { crossdomain: true });                
                dispatch('alert/setLoading2', false, {root: true});
                dispatch('alert/setLoaded2', true, {root: true});
                return response.data;
            } catch(err) {
                console.log('Error while saving Practice Pulse checkout notes:', err);                
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw err;
            }
        },

        async getPatientData({ commit, rootState, dispatch}, payload) {
            try {                
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post(`/get-practice-pulse-patient-data`, payload, { crossdomain: true });                
                dispatch('alert/setLoading2', false, {root: true});
                dispatch('alert/setLoaded2', true, {root: true});
                return response.data;
            } catch(err) {
                console.log('Error while getting patient data in Practice Pulse:', err);                
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
            }
        },
    }
};